import { eventToContentSources } from "../ServiceWorker/networkEventToContentSources";
import { DbRecordedEvent, ISODateTimeString } from "../State/EvidenceDB/DatabaseTypes";
import { ContentSource, enhancedContentSourceLocation, inferDataTypeNameFromContentSource } from "./ContentSource";
import { EfficientStringSearchResult, stringFoundMatchesCaseSensitively } from "./EfficientMultiStringSearch";
import { NetworkEvent } from "./Events";
import { EfficientEncodedStringSearchFunction } from "./Search/EfficientEncodedStringSearchResult";
import { EncodedSearchTermWithEncodingSteps } from "./Search/EncodedSearchTerm";
import { hasCodeUrls } from "./utils/CodeLocation";
import { urlToURL } from "./utils/domains";

export interface SearchContext {
	prefix: string;
	suffix: string;
}

export type ContentSourceSearchResult = EncodedSearchTermWithEncodingSteps &
	EfficientStringSearchResult &
	SearchContext &
	ContentSource & {
		inferredDataTypeName?: string;
	};
export const ContentSourceSearchResultKey = (o: ContentSourceSearchResult) =>
	`ContentSourceSearchResult:\0${
		o.searchTermAsSpecified}\0;${
		enhancedContentSourceLocation(o) as string}\0;${
		o.position
}` as const;
export type ContentSourceSearchResultKey = ReturnType<typeof ContentSourceSearchResultKey>;

export interface OriginAndDestinationUrl {
	originUrl: string;
	destinationUrl: string;
}

export interface NetworkRequestInformation extends OriginAndDestinationUrl {
	eventIndex: number;
	requestId: string;
	whenRecordedIso: ISODateTimeString;
	codeUrls?: string[];
}

export const NetworkRequestInformation = (networkEvent: DbRecordedEvent<NetworkEvent>): NetworkRequestInformation => ({
	eventIndex: networkEvent.eventIndex,
	requestId: networkEvent.raw.requestId,
	whenRecordedIso: networkEvent.whenRecordedIso,
	originUrl: networkEvent.tab.url,
	destinationUrl: networkEvent.destination.url,
	...( hasCodeUrls(networkEvent) ? { codeUrls: networkEvent.codeUrls } : {})
})

export interface HasSearchResult {
	searchResult: ContentSourceSearchResult;	
}

export interface HasSearchResults {
	searchResults: ContentSourceSearchResult[];
}

export type EventSearchResults =
	HasSearchResults & NetworkRequestInformation;

export type EventIndexOnly = {eventIndex: number};

export type EventSearchResultsOrEventIndexOnly = EventSearchResults | EventIndexOnly;

export const EventSearchResults = (o: unknown): o is EventSearchResults =>
	typeof o === 'object' && o != null && 'searchResults' in o && 'eventIndex' in o &&
	Array.isArray(o.searchResults) && typeof o.eventIndex === "number";

export type EventSearchResult =
	HasSearchResult & NetworkRequestInformation;

export const EventSearchResultKey = (o: EventSearchResult) =>
	`SearchResultKey\0${
		// Zero-pad the event index to ensure sort order matches numerical order for up to a billion events
		o.eventIndex.toString().padStart(9, '0') as unknown as number}\0${
		o.searchResult.stringFound}\0${
		enhancedContentSourceLocation(o.searchResult) as string}\0${
		// Zero-pad the position to ensure sort order matches numerical order for up to a billion characters
		o.searchResult.position.toString().padStart(9, '0') as unknown as number
	}` as const;
/**
 * A search result key, which is a string designed so that it's sort order will put events with earlier
 * eventIndex values before those with later eventIndex values.
 */
export type EventSearchResultKey = ReturnType<typeof EventSearchResultKey>;

export interface SearchContextOptions {
	prefixSize?: number;
	suffixSize?: number;
}

export interface NetworkSearchScopeOptions {
	searchDataReceived?: boolean;
}


export interface SearchTermsArgument {
	searchTerms: string[];
}export interface SearchOptions extends SearchContextOptions, NetworkSearchScopeOptions {
	maxResultsPerSearchTerm?: number;
}


export const SearchableUrlProtocolPrefixes = [
	'http:',
	'https:',
	'ws:',
	'wss:'
] as const;
export type SearchableUrlProtocolPrefix = typeof SearchableUrlProtocolPrefixes[number];
const SearchableUrlProtocolPrefixSet = new Set(SearchableUrlProtocolPrefixes);
export const isSearchableUrlProtocolPrefix = (protocol: unknown): protocol is SearchableUrlProtocolPrefix =>
	SearchableUrlProtocolPrefixSet.has(protocol as SearchableUrlProtocolPrefix);

export const isSearchablePageUrl = (url: string) =>
	isSearchableUrlProtocolPrefix(urlToURL(url)?.protocol);


export const searchNetworkEventFn = (
	searchFunction: EfficientEncodedStringSearchFunction,
	{prefixSize = 72, suffixSize = 72, searchDataReceived = false}: SearchOptions = {}
) => async (event: DbRecordedEvent<NetworkEvent>): Promise<ContentSourceSearchResult[]> => {
	const resultsByKey = new Map<ContentSourceSearchResultKey, ContentSourceSearchResult>();
	const contentSources = await eventToContentSources(event);
	for (const contentSource of contentSources) {

		// Only search through content sent out
		if (contentSource.direction === 'received' && !searchDataReceived) {
			// Skip inbound traffic
			continue;
		}
		// Update progress counters
		// let seenInThisContentSource: undefined | Set<string>;

		// Search the source to find new results
		for (const result of searchFunction(contentSource.content)) {
			// let newResultsFoundFromSearch = searchFunction(contentSource.content).map((result) => {
			const { validPrefixCharacters, validSuffixCharacters, encodedSearchTerm, position, encodingSteps } = result;

			if (!stringFoundMatchesCaseSensitively(result) && encodingSteps[encodingSteps.length - 1]?.encodingType === 'base64') {
				// Base64 is a case-sensitive encoding, so reject all matches that do not match case sensitively.
				continue

			}
			// Validate any prefix/suffix requirements on this result
			if (validPrefixCharacters != null) {
				// This results is for a search term that is only valid if it is preceded by a character in the set of validPrefixCharacters.
				const prefixChar = contentSource.content.charAt(position - 1);
				if (!validPrefixCharacters.has(prefixChar)) {
					// Throw the result away because it is not a complete max: the prefix character is not valid.
					continue
				}
			}
			if (validSuffixCharacters != null) {
				// This results is for a search term that is only valid if it is followed by a character in the set of validSuffixCharacters.
				const suffixChar = contentSource.content.charAt(position + encodedSearchTerm.length);
				if (!validSuffixCharacters.has(suffixChar)) {
					// Throw the result away because it is not a complete max: the suffix character is not valid.
					continue
				}
			}

			// Copy a prefix from before the result and a suffix from after it to provide context
			const prefix = contentSource.content.slice(Math.max(0, position - prefixSize), position);
			const suffix = contentSource.content.slice(position + encodedSearchTerm.length, position + encodedSearchTerm.length + suffixSize);

			// Infer a data type name
			const inferredDataTypeName = position >= 1 ? undefined : inferDataTypeNameFromContentSource(contentSource);
			const contentSourceSearchResult: ContentSourceSearchResult = {
				prefix, suffix,
				...(inferredDataTypeName == null ? {} : { inferredDataTypeName }),
				...result, ...contentSource
			}
			const key = ContentSourceSearchResultKey(contentSourceSearchResult);
			if ((resultsByKey.get(key)?.stringFound.length ?? -1) >= contentSourceSearchResult.stringFound.length) {
				continue;
			} else {
				resultsByKey.set(key, {
					prefix, suffix,
					...(inferredDataTypeName == null ? {} : { inferredDataTypeName }),
					...result, ...contentSource
				} satisfies ContentSourceSearchResult);
			}
		}
	}

	if (resultsByKey.size === 0) {
		// No need to filter empty array
		return [];
	}


	const searchTermsFound = new Set<string>();
	const results = [...resultsByKey.values()].filter( r => {
		if (searchTermsFound.has(r.searchTermAsSpecified)) {
			// This search term has already been found in this event, so skip it.
			return false;
		} else {
			searchTermsFound.add(r.searchTermAsSpecified);
			return true;
		}
	});


	return results;
};

export type SearchNetworkEventAndEmbedEventInResults = (event: DbRecordedEvent<NetworkEvent>) => Promise<EventSearchResultsOrEventIndexOnly>;

export const searchNetworkEventAndEmbedEventInResultsFn = (
	searchFunction: EfficientEncodedStringSearchFunction,
	options?: SearchOptions
): SearchNetworkEventAndEmbedEventInResults => {
	const searchNetworkEvent = searchNetworkEventFn(searchFunction, options);
	return async (
		event: DbRecordedEvent<NetworkEvent>
	) => {
		const {eventIndex} = event;
		const searchResults = await searchNetworkEvent(event);
		if (searchResults.length === 0) {
			return {eventIndex};
		} else {
			// ProcessInfo.log(`Found results network event ${event.eventIndex} (${event.raw.requestId})`, event, searchResults);
			return {
				...NetworkRequestInformation(event), searchResults
			} satisfies EventSearchResults;
		}
	}
};

